import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { BoardModel } from "models/BoardModel";
import { useApi } from "providers/Api";
import { useCustomParams } from "providers/Params";
import { useToasts } from "providers/Toasts";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useCallback, useMemo } from "react";

import { BoardContext } from "./BoardContext";

export const BoardProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { boardApi } = useApi();
  const { boardId } = useCustomParams();
  const i18n = useTranslations();
  const toasts = useToasts();

  const { isLoading, refetch, data, error } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () => {
      return boardApi.getBoard(boardId);
    },
    enabled: !!boardId,
  });

  const reloadBoard = useCallback(async () => {
    const result = await refetch();

    const newBoard = result?.data as BoardModel;
    if (newBoard) {
      toasts.refreshSuccess(i18n.Board, newBoard?.name);
    }
  }, [refetch, i18n, toasts]);

  const contextValue = useMemo(
    () => ({
      boardId,
      board: isLoading ? undefined : data,
      error: error as AxiosError | undefined,
      isLoading,
      reloadBoard,
    }),
    [boardId, data, isLoading, error, reloadBoard]
  );

  return <BoardContext.Provider value={contextValue}>{children}</BoardContext.Provider>;
};
